<template>
  <AlDialog
    v-model="dialog"
    title="Approval Summary"
    persistent
    max-width="700"
    :error="error"
    @clearErrors="clearErrors()"
  >
    <v-form ref="formRef" @submit.prevent="submit">
      <div class="pa-4">
        <p class="mb-4 text-body-1">Please provide a summary for approving this report.</p>
        <v-textarea
          v-model="reportSummaryFormData.description"
          @input="clearErrors()"
          :error-messages="error && (error.fieldErrors['description'] as string[])"
          label="Approval Summary"
          variant="outlined"
          rows="4"
          counter="1000"
          maxlength="1000"
        ></v-textarea>
      </div>
    </v-form>

    <template #actions>
      <v-spacer></v-spacer>
      <v-btn color="primary" variant="flat" :loading="loading" @click="submit">Save</v-btn>
    </template>
  </AlDialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { cloneDeep } from 'lodash'
import type { VForm } from 'vuetify/components'

import { ReportSummary, type IReportSummary, type IReportSummaryData } from '@/models/report'
import type { ISystemError } from '@/models/error'

import AlDialog from '@/components/common/AlDialog.vue'

const dialog = defineModel<boolean>('dialog', { required: true })

interface Props {
  reportSummary?: IReportSummary
  loading?: boolean
  isEdit: boolean
}
const props = defineProps<Props>()

interface Emit {
  (e: 'save', summary?: IReportSummary): void
}
const emit = defineEmits<Emit>()

const error = defineModel<ISystemError | null>('error', { default: null })

const formRef = ref<VForm>()
const reportSummaryFormData = ref<IReportSummaryData>({})

function clearErrors() {
  error.value = null
}

watch(
  () => dialog.value,
  () => {
    reportSummaryFormData.value = props.isEdit
      ? cloneDeep(props.reportSummary!)
      : ({} as IReportSummaryData)
  }
)

async function submit() {
  if (!reportSummaryFormData.value.description && !props.isEdit) {
    emit('save')
    return
  }

  const { valid } = await formRef.value!.validate()

  if (valid) {
    let reportSummary: IReportSummary | null = null

    try {
      reportSummary = new ReportSummary(reportSummaryFormData.value)
    } catch (e: any) {
      error.value = e
    }

    if (reportSummary != null) {
      emit('save', reportSummary)
    }
  }
}
</script>
